import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { SquarePenIcon } from 'lucide-preact';
import { ComponentProps } from 'preact';

import { cn } from '@/lib/utils';
import { getMediaQueryOptions } from '@/queries/media';
import type { Media } from '@/queries/media/types';
import { useCan } from '@/shared/entitlements';

import AnimalIcon from '../AnimalIcon';
import EditVideoModal from '../EditVideoModal';
import Loader from '../Loader';
import MediaScheduleTag from '../MediaCollectionView/MediaScheduleTag';
import MobileMediaCard from '../MediaCollectionView/MobileMediaCard';
import RoundButton from '../RoundButton';

export interface MediaCellProps extends Omit<ComponentProps<'a'>, 'media'> {
  media?: Media;
  mediaId: string;
  isPlaying?: boolean;
}

const MediaCell = ({ media: mediaProp, mediaId, isPlaying, className, ...anchorProps }: MediaCellProps) => {
  const can = useCan();

  const { data: fetchedMedia, isPending } = useQuery({
    ...getMediaQueryOptions(mediaProp ? undefined : mediaId),
    enabled: Boolean(mediaId) && !mediaProp,
  });

  const media = mediaProp ?? fetchedMedia;

  if (!media) {
    return (
      <a
        className={cn(
          'preflight preflight-a group relative block gap-4 aria-disabled:opacity-50 mobile:grid mobile:grid-cols-[150px_1fr]',
          className,
        )}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...anchorProps}
      >
        {isPending ? (
          <div className="flex aspect-video w-full items-center justify-center rounded bg-black/5">
            <Loader color="var(--charcoal)" width="40px" height="40px" />
          </div>
        ) : null}
      </a>
    );
  }

  const isVideo = media.mediaType === 'video';
  // Asked about the habitat the media belongs to rather than the one on screen: a cell shows up in
  // My Album and in search results, where there is no open habitat at all.
  const canEditDetails = can('media.editDetails', media.habitatId);

  return (
    <a
      aria-disabled={media.disabled}
      // The tile is the thumbnail, and its alt is the Caption — which is optional, so without this
      // the link can end up with no accessible name at all.
      aria-label={media.title?.trim() || 'Open media'}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...anchorProps}
      className={`preflight preflight-a group relative block gap-4 aria-disabled:opacity-50 mobile:grid mobile:grid-cols-[150px_1fr] ${
        className ?? ''
      }`}
    >
      {/* Desktop Image + Overlay (Hidden on Mobile) */}
      <div className="preflight relative hidden overflow-hidden rounded desktop:block desktop:rounded-lg">
        <img className="preflight preflight-img aspect-video" src={media.thumbnailURL} alt={media.title} loading="lazy" />
        {isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/75">
            <div className="flex h-6 items-end gap-0.5 desktop:h-10">
              <span className="h-[60%] w-2 animate-soundbar bg-white desktop:w-4" />
              <span className="h-[30%] w-2 animate-soundbar bg-white [animation-delay:-2.2s] desktop:w-4" />
              <span className="h-[75%] w-2 animate-soundbar bg-white [animation-delay:-3.7s] desktop:w-4" />
            </div>
          </div>
        )}
        {/* Desktop hover overlay: User badge, username, time and description */}
        <div className="pointer-events-none absolute inset-0 hidden desktop:block">
          {media.habitat?.title && (
            <div className="absolute left-4 top-3 z-[5] flex h-[18px] items-center justify-center rounded-[3px] bg-white/50 px-2 opacity-0 backdrop-blur-[2px] transition-opacity group-hover:opacity-100">
              <span className="block text-[10px] font-bold leading-none text-black">{media.habitat.title}</span>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 translate-y-full transform bg-gradient-to-t from-[rgb(38,38,38)] from-[17%] to-100% px-4 pb-4 pt-6 text-white transition group-hover:translate-y-0 group-hover:opacity-100">
            <div className="flex items-center gap-1.5">
              {media.user?.profile && (
                <AnimalIcon
                  animalIcon={media.user.profile.animalIcon}
                  color={media.user.profile.color}
                  gradient={media.user.profile.gradient}
                  width={24}
                />
              )}
              <span className="text-[10px] font-medium text-white">
                {media.user?.username} at {format(new Date(media.publishedAt), 'ha').toLowerCase()}{' '}
                {format(new Date(media.publishedAt), 'MMM d')}
              </span>
            </div>
            {/* Optional Caption — omit the element rather than leaving an empty bold line with a
                margin. The username and publish time above already identify the media. */}
            {!!media.title?.trim() && (
              <p className="preflight preflight-p mt-1 line-clamp-2 text-[13px] font-bold leading-tight text-white">
                {media.title}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Media Card (Hidden on Desktop) */}
      <div className="contents desktop:hidden">
        <MobileMediaCard video={media} isPlaying={isPlaying} />
      </div>

      {canEditDetails && isVideo && (
        <div className="pointer-events-none absolute right-2 top-2 z-[3] hidden flex-row-reverse gap-3 desktop:group-hover:flex md:right-4 md:top-4">
          <EditVideoModal.Trigger
            videoId={media._id}
            defaultThumbnailURL={media.thumbnailURL}
            defaultTitle={media.title}
            defaultPublishedAt={media.publishedAt}
            canEditPlaylists={!media.disabled}
            render={(setIsOpen) => (
              <RoundButton
                width={24}
                color="var(--blueDark)"
                backgroundColor="var(--white)"
                className="pointer-events-auto"
                onClick={(e: MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsOpen(true);
                }}
              >
                <SquarePenIcon className="size-[1em] text-xs" />
                <span className="sr-only">Edit</span>
              </RoundButton>
            )}
          />
        </div>
      )}
      <MediaScheduleTag publishedAt={media.publishedAt} />
    </a>
  );
};

export default MediaCell;
